import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type CreateMaterialInput, type UpdateMaterialInput, type CreateCouponInput, type UpdateCouponInput, type ReviewQuoteInput } from '@/services/admin';
import { MATERIALS_QUERY_KEY } from './useMaterials';

export const useDashboardStats = (days = 30) => {
  return useQuery({
    queryKey: ['admin', 'stats', days],
    queryFn: () => adminApi.getDashboardStats(days),
    staleTime: 1000 * 30,
  });
};

export const useAdminOrders = (status?: string) => {
  return useQuery({
    queryKey: ['admin', 'orders', status],
    queryFn: () => adminApi.listOrders({ status, limit: 50 }),
  });
};

export const useAdminQuotes = (status?: string) => {
  return useQuery({
    queryKey: ['admin', 'quotes', status],
    queryFn: () => adminApi.listQuotes({ status, limit: 50 }),
  });
};

export const useAdminQuote = (quoteId: string | null) => {
  return useQuery({
    queryKey: ['admin', 'quote', quoteId],
    queryFn: () => adminApi.getQuote(quoteId!),
    enabled: !!quoteId,
  });
};

export const useReviewQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quoteId,
      input,
    }: {
      quoteId: string;
      input: ReviewQuoteInput;
    }) => adminApi.reviewQuote(quoteId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'quotes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'quote'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
};

export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      status,
    }: {
      orderId: string;
      status: string;
    }) => adminApi.updateOrderStatus(orderId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
};

export const useUpdateTracking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      trackingNumber,
      trackingCarrier,
    }: {
      orderId: string;
      trackingNumber: string;
      trackingCarrier?: string;
    }) => adminApi.updateTracking(orderId, trackingNumber, trackingCarrier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
};

export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      action,
      reason,
    }: {
      orderId: string;
      action: 'cancel' | 'reject';
      reason: string;
    }) => adminApi.cancelOrder(orderId, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
};

export const useAdminMaterials = () => {
  return useQuery({
    queryKey: ['admin', 'materials'],
    queryFn: () => adminApi.getMaterials(),
  });
};

export const useUpdateMaterialStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
      updates,
    }: {
      materialId: string;
      updates: { colorStock?: Record<string, boolean>; isActive?: boolean };
    }) => adminApi.updateMaterialStock(materialId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
      queryClient.invalidateQueries({ queryKey: MATERIALS_QUERY_KEY });
    },
  });
};

export const useCreateMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMaterialInput) => adminApi.createMaterial(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
      queryClient.invalidateQueries({ queryKey: MATERIALS_QUERY_KEY });
    },
  });
};

export const useUpdateMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
      updates,
    }: {
      materialId: string;
      updates: UpdateMaterialInput;
    }) => adminApi.updateMaterial(materialId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
      queryClient.invalidateQueries({ queryKey: MATERIALS_QUERY_KEY });
    },
  });
};

export const useDeleteMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (materialId: string) => adminApi.deleteMaterial(materialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] });
      queryClient.invalidateQueries({ queryKey: MATERIALS_QUERY_KEY });
    },
  });
};

export const useUpdateProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: Record<string, unknown>;
    }) => adminApi.updateProduction(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
};

export const useUpdateShipping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: Record<string, unknown>;
    }) => adminApi.updateShipping(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
};

export const useUpdatePricing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: Record<string, unknown>;
    }) => adminApi.updatePricing(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
};

export const useAuditTrail = (entityId: string | null) => {
  return useQuery({
    queryKey: ['admin', 'audit', entityId],
    queryFn: () => adminApi.getAuditTrail(entityId!),
    enabled: !!entityId,
  });
};

export const useQueue = () => {
  return useQuery({
    queryKey: ['admin', 'queue'],
    queryFn: () => adminApi.getQueue(),
  });
};

export const useUpdateQueue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: readonly Record<string, unknown>[]) =>
      adminApi.updateQueue(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'queue'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};

export const useFailureAnalytics = () => {
  return useQuery({
    queryKey: ['admin', 'analytics', 'failures'],
    queryFn: () => adminApi.getFailureAnalytics(),
    staleTime: 1000 * 60,
  });
};

export const useWorkflowConfig = () => {
  return useQuery({
    queryKey: ['admin', 'config', 'workflow'],
    queryFn: () => adminApi.getWorkflowConfig(),
  });
};

export const useUpdateWorkflowConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Record<string, unknown>) =>
      adminApi.updateWorkflowConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config', 'workflow'] });
    },
  });
};

export const useWebhookErrors = (date?: string) => {
  return useQuery({
    queryKey: ['admin', 'webhook-errors', date],
    queryFn: () => adminApi.getWebhookErrors(date),
  });
};

// =============================================
// Coupon hooks
// =============================================

export const useAdminCoupons = () => {
  return useQuery({
    queryKey: ['admin', 'coupons'],
    queryFn: () => adminApi.listCoupons(),
  });
};

export const useCreateCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCouponInput) => adminApi.createCoupon(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
};

export const useUpdateCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      code,
      updates,
    }: {
      code: string;
      updates: UpdateCouponInput;
    }) => adminApi.updateCoupon(code, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
};

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => adminApi.deleteCoupon(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
};
